"use client";

import React from 'react';
import { cn } from '../../lib/utils';
import { motion, MotionProps } from 'framer-motion';
import { cva, VariantProps } from 'class-variance-authority';
import { spanTypography } from '../../styles/typography';

const animationProps = {
  initial: { "--x": "100%" },
  animate: { "--x": "-100%" },
  whileTap: { scale: 0.95 },
  transition: {
    repeat: Infinity,
    repeatType: "loop" as const,
    repeatDelay: 1,
    type: "spring" as const,
    stiffness: 20,
    damping: 15,
    mass: 2,
  },
};

const secondaryButtonVariants = cva(
  cn(
    "relative cursor-pointer font-[var(--font-weight-medium)] backdrop-blur-xl transition-shadow duration-300 ease-in-out hover:shadow-lg overflow-hidden",
    "flex items-center justify-center gap-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0"
  ),
  {
    variants: {
      size: {
        default: "px-6 py-2 rounded-lg text-[var(--font-size-sm)] font-[var(--font-family-primary)]",
        sm: "px-4 h-8 rounded-xl text-[var(--font-size-xs)] font-[var(--font-family-primary)]",
        lg: "px-8 py-3 rounded-xl text-[var(--font-size-base)] font-[var(--font-family-primary)]",
        profile: "flex-1 px-4 py-3 rounded-xl text-[var(--font-size-sm)] font-[var(--font-family-primary)] flex items-center space-x-2 min-w-0"
      },
      variant: {
        default: "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-50",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground"
      }
    },
    defaultVariants: {
      size: "default",
      variant: "default",
    },
  },
);

interface SecondaryButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof MotionProps>,
    MotionProps,
    VariantProps<typeof secondaryButtonVariants> {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

const SecondaryButton = React.forwardRef<HTMLButtonElement, SecondaryButtonProps>(
  ({ children, className, size, variant, ...props }, ref) => {
    return (
      <motion.button
        ref={ref}
        className={cn(secondaryButtonVariants({ size, variant }), className)}
        {...animationProps}
        {...props}
      >
        <span className={cn(
          "relative flex items-center justify-center gap-2 size-full font-[var(--font-weight-medium)] font-[var(--font-family-primary)] tracking-wide z-20",
          size === 'profile' ? spanTypography.buttonSpan.secondary : ""
        )}>
          {children}
        </span>
        <span
          style={{
            backgroundImage:
              "linear-gradient(-75deg,transparent calc(var(--x)+20%),rgba(255,255,255,0.8) calc(var(--x)+25%),transparent calc(var(--x)+100%))",
          }}
          className="absolute inset-0 z-10 block rounded-[inherit]"
        />
      </motion.button>
    );
  }
);

SecondaryButton.displayName = "SecondaryButton";

export { SecondaryButton, secondaryButtonVariants };
export type { SecondaryButtonProps };